import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { adminAPI } from '../../services/api';
import { 
  MessageCircle, Heart, Repeat2, Send, Search, TrendingUp, Users, 
  CheckCircle, Plus, X, Image as ImageIcon, Video, DollarSign, 
  UserPlus, Eye, Sparkles, Flag, MoreVertical, Ban
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function EnhancedConnectFeed() {
  const [activeTab, setActiveTab] = useState('for-you');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [newPost, setNewPost] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [suggestedPeople, setSuggestedPeople] = useState([]);
  const [showTipDialog, setShowTipDialog] = useState(false);
  const [tipPostId, setTipPostId] = useState(null);
  const [tipAmount, setTipAmount] = useState('');
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reportCategory, setReportCategory] = useState('spam');
  const [showPostMenu, setShowPostMenu] = useState(null);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadFeed();
    loadSuggestedPeople();
  }, [activeTab]);

  const loadFeed = async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getConnectFeed(activeTab);
      setPosts(res.data.posts || []);
    } catch (error) {
      console.error('Failed to load feed:', error);
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestedPeople = async () => {
    try {
      const res = await adminAPI.getSuggestedPeople(5);
      setSuggestedPeople(res.data.suggested_people || []);
    } catch (error) {
      console.error('Failed to load suggestions:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');

    if (!isImage && !isVideo) {
      toast.error('Please select an image or video file');
      return;
    }

    // Size limits
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large (max ${isVideo ? '50' : '10'}MB)`);
      return;
    }

    setMediaFile(file);
    setMediaType(isVideo ? 'video' : 'photo');
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setMediaPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const clearMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
    setMediaType(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCreatePost = async () => {
    if (!newPost.trim() && !mediaFile) {
      toast.error('Post cannot be empty');
      return;
    }

    if (newPost.length > 280) {
      toast.error('Post is too long (max 280 characters)');
      return;
    }

    try {
      setUploading(true);
      let mediaUrl = null;
      
      // Upload media if present
      if (mediaFile) {
        const reader = new FileReader();
        reader.readAsDataURL(mediaFile);
        await new Promise((resolve) => {
          reader.onload = async () => {
            const base64 = String(reader.result).split(',')[1];
            const res = await adminAPI.uploadPostMedia(
              mediaType,
              mediaFile.name,
              mediaFile.type,
              base64
            );
            mediaUrl = res.data.media_url;
            resolve();
          };
        });
      }

      // Create post
      await adminAPI.createPost(newPost.trim(), null, mediaUrl, mediaType);
      toast.success('Post created!');
      setNewPost('');
      clearMedia();
      setShowCreatePost(false);
      loadFeed();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setUploading(false);
    }
  };

  const handleLike = async (postId, isLiked) => {
    try {
      if (isLiked) {
        await adminAPI.unlikePost(postId);
      } else {
        await adminAPI.likePost(postId);
      }
      setPosts(posts.map(p => p.id === postId ? {
        ...p,
        liked_by_me: !isLiked,
        likes_count: isLiked ? p.likes_count - 1 : p.likes_count + 1
      } : p));
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const handleTip = async () => {
    const amount = parseFloat(tipAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      await adminAPI.tipPost(tipPostId, amount);
      toast.success('Tip sent!');
      setShowTipDialog(false);
      setTipPostId(null);
      setTipAmount('');
      loadFeed();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send tip');
    }
  };

  const handleFollow = async (username) => {
    try {
      await adminAPI.followUser(username);
      toast.success(`Following @${username}`);
      loadSuggestedPeople();
    } catch (error) {
      toast.error('Failed to follow');
    }
  };

  const handleReport = async () => {
    if (!reportReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }

    try {
      await adminAPI.reportContent(
        reportTarget.type,
        reportTarget.id,
        reportReason,
        reportCategory,
        null
      );
      toast.success('Report submitted. Our team will review it.');
      setShowReportDialog(false);
      setReportTarget(null);
      setReportReason('');
      setReportCategory('spam');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    }
  };

  const handleBlock = async (username) => {
    if (!window.confirm(`Block @${username}? They won't be able to see your posts or interact with you.`)) {
      return;
    }

    try {
      await adminAPI.blockUser(username);
      toast.success(`Blocked @${username}`);
      loadFeed();
    } catch (error) {
      toast.error('Failed to block user');
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Feed */}
          <div className="lg:col-span-2 space-y-4">
            {/* Header */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-2xl">Matrise Connect</CardTitle>
                  <Button onClick={() => setShowCreatePost(true)} size="sm">
                    <Plus className="h-4 w-4 mr-1" /> New Post
                  </Button>
                </div>
              </CardHeader>
            </Card>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="for-you">
                  <Sparkles className="h-4 w-4 mr-1" /> For You
                </TabsTrigger>
                <TabsTrigger value="following">
                  <Users className="h-4 w-4 mr-1" /> Following
                </TabsTrigger>
                <TabsTrigger value="everyone">
                  <Eye className="h-4 w-4 mr-1" /> Everyone
                </TabsTrigger>
                <TabsTrigger value="trending">
                  <TrendingUp className="h-4 w-4 mr-1" /> Trending
                </TabsTrigger>
              </TabsList>

              <TabsContent value={activeTab} className="space-y-4 mt-4">
                {loading ? (
                  <Card><CardContent className="p-8 text-center">Loading...</CardContent></Card>
                ) : posts.length === 0 ? (
                  <Card><CardContent className="p-8 text-center text-muted-foreground">No posts yet</CardContent></Card>
                ) : (
                  posts.map((post) => (
                    <Card key={post.id} className="overflow-hidden">
                      <CardContent className="p-4">
                        {/* Post Header */}
                        <div className="flex items-start gap-3 mb-3">
                          <Avatar className="h-10 w-10" onClick={() => navigate(`/app/profile/${post.username}`)}>
                            <AvatarImage src={post.profile_picture} />
                            <AvatarFallback>{post.display_name?.[0] || post.username[0]}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold cursor-pointer hover:underline" onClick={() => navigate(`/app/profile/${post.username}`)}>
                                {post.display_name || post.username}
                              </span>
                              {post.verified && <CheckCircle className="h-4 w-4 text-blue-500" />}
                              <span className="text-sm text-muted-foreground">@{post.username}</span>
                              <span className="text-sm text-muted-foreground">·</span>
                              <span className="text-sm text-muted-foreground">{formatTimeAgo(post.created_at)}</span>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setShowPostMenu(showPostMenu === post.id ? null : post.id)}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                          {showPostMenu === post.id && (
                            <div className="absolute right-4 mt-8 w-48 bg-background border rounded-lg shadow-lg z-10">
                              <button
                                className="w-full px-4 py-2 text-left hover:bg-muted flex items-center gap-2"
                                onClick={() => {
                                  setReportTarget({ type: 'post', id: post.id });
                                  setShowReportDialog(true);
                                  setShowPostMenu(null);
                                }}
                              >
                                <Flag className="h-4 w-4" /> Report Post
                              </button>
                              <button
                                className="w-full px-4 py-2 text-left hover:bg-muted flex items-center gap-2"
                                onClick={() => {
                                  handleBlock(post.username);
                                  setShowPostMenu(null);
                                }}
                              >
                                <Ban className="h-4 w-4" /> Block @{post.username}
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Post Content */}
                        <div className="mb-3">
                          <p className="whitespace-pre-wrap">{post.content}</p>
                        </div>

                        {/* Media */}
                        {post.media_url && (
                          <div className="mb-3 rounded-lg overflow-hidden border">
                            {post.media_type === 'photo' ? (
                              <img src={post.media_url} alt="Post media" className="w-full" />
                            ) : post.media_type === 'video' ? (
                              <video src={post.media_url} controls className="w-full" />
                            ) : null}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-6 text-muted-foreground">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleLike(post.id, post.liked_by_me)}
                            className={post.liked_by_me ? 'text-red-500' : ''}
                          >
                            <Heart className={`h-4 w-4 mr-1 ${post.liked_by_me ? 'fill-current' : ''}`} />
                            {post.likes_count || 0}
                          </Button>
                          <Button variant="ghost" size="sm">
                            <MessageCircle className="h-4 w-4 mr-1" />
                            {post.comments_count || 0}
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Repeat2 className="h-4 w-4 mr-1" />
                            {post.reposts_count || 0}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setTipPostId(post.id);
                              setShowTipDialog(true);
                            }}
                          >
                            <DollarSign className="h-4 w-4 mr-1" />
                            {post.total_tipped > 0 ? `${post.total_tipped} ZAR` : 'Tip'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Suggested People */}
            {suggestedPeople.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Suggested for you</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {suggestedPeople.map((person) => (
                    <div key={person.id} className="flex items-center gap-3">
                      <Avatar className="h-10 w-10" onClick={() => navigate(`/app/profile/${person.username}`)}>
                        <AvatarImage src={person.profile_picture} />
                        <AvatarFallback>{person.display_name[0]}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate cursor-pointer hover:underline" onClick={() => navigate(`/app/profile/${person.username}`)}>
                          {person.display_name}
                        </p>
                        <p className="text-sm text-muted-foreground truncate">@{person.username}</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => handleFollow(person.username)}>
                        <UserPlus className="h-3 w-3 mr-1" /> Follow
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Create Post Dialog */}
      <Dialog open={showCreatePost} onOpenChange={setShowCreatePost}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="What's on your mind?"
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              rows={4}
              maxLength={280}
            />
            <div className="flex justify-between items-center">
              <div className="text-xs text-muted-foreground">
                ⚠️ Be respectful. Spam, harassment, and inappropriate content will be removed.
              </div>
              <div className="text-sm text-muted-foreground">
                {newPost.length}/280
              </div>
            </div>

            {/* Media Preview */}
            {mediaPreview && (
              <div className="relative rounded-lg overflow-hidden border">
                {mediaType === 'photo' ? (
                  <img src={mediaPreview} alt="Preview" className="w-full" />
                ) : (
                  <video src={mediaPreview} controls className="w-full" />
                )}
                <Button
                  size="icon"
                  variant="destructive"
                  className="absolute top-2 right-2"
                  onClick={clearMedia}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Media Buttons */}
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={!!mediaFile}
              >
                <ImageIcon className="h-4 w-4 mr-1" /> Photo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={!!mediaFile}
              >
                <Video className="h-4 w-4 mr-1" /> Video
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreatePost(false)}>Cancel</Button>
            <Button onClick={handleCreatePost} disabled={uploading || (!newPost.trim() && !mediaFile)}>
              {uploading ? 'Posting...' : 'Post'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tip Dialog */}
      <Dialog open={showTipDialog} onOpenChange={setShowTipDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Tip</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="number"
              placeholder="Amount (ZAR)"
              value={tipAmount}
              onChange={(e) => setTipAmount(e.target.value)}
              min="1"
              step="1"
            />
            <div className="flex gap-2">
              {[10, 20, 50, 100].map(amt => (
                <Button key={amt} variant="outline" size="sm" onClick={() => setTipAmount(amt.toString())}>
                  R{amt}
                </Button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTipDialog(false)}>Cancel</Button>


      {/* Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Report Content</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Category</label>
              <select
                className="w-full mt-1 p-2 border rounded"
                value={reportCategory}
                onChange={(e) => setReportCategory(e.target.value)}
              >
                <option value="spam">Spam</option>
                <option value="harassment">Harassment</option>
                <option value="inappropriate">Inappropriate Content</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Reason</label>
              <Textarea
                placeholder="Please describe the issue..."
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>Cancel</Button>
            <Button onClick={handleReport}>Submit Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

            <Button onClick={handleTip}>Send Tip</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default EnhancedConnectFeed;
